import { DialogBody, DialogFooter, IconButton } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { getProject as apiGetProject } from '~/api/projects2';
import type { Project } from '~/common/types';
import { Dropdown } from '~/components2/Dropdown';
import { useIsSandboxOrg } from '~/hooks/useIsSandboxOrg';
import { useLandRightsTitleZipsViaS3Enabled } from '~/hooks/useLandRightsTitleZipsViaS3Enabled';
import type { ProjectDetailsParams } from '~/pages/projectDetails';
import { Buttons } from '~/pages/projectDetails/landRights/Buttons';
import { ConfirmDocumentOrderModal } from '~/pages/projectDetails/landRights/ConfirmDocumentOrderModal';
import { ConfirmDocumentsOrderModal } from '~/pages/projectDetails/landRights/ConfirmDocumentsOrderModal';
import { Content } from '~/pages/projectDetails/landRights/Content';
import {
  buildParcelRequest,
  getParcelIdsFromSelectedIds,
  pickFatZip,
  reprintTitle,
} from '~/pages/projectDetails/landRights/helper';
import { DocType, OrderStep } from '~/pages/projectDetails/landRights/types';
import rootStore from '~/store';
import store, { type ParcelDetails } from './store';

type DocParams = { DocId: string; CmtId: string };

export default function GetDocuments() {
  const { currentOrganization } = useSnapshot(rootStore);
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { parcelDetails, selectedIds } = useSnapshot(store);
  const [isOpen, setIsOpen] = useState(false);
  const [project, setProject] = useState<Project>();
  const [selectedParcelAPNs, setSelectedParcelAPNs] = useState([] as string[]);
  const titleZipsViaS3 = useLandRightsTitleZipsViaS3Enabled();
  const isSandboxOrg = useIsSandboxOrg();
  const [docToOrder, setDocToOrder] = useState<DocParams>();
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [docsToOrder, setDocsToOrder] = useState<DocParams[]>([]);
  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const data = await getParcelIdsFromSelectedIds(selectedIds);
      setSelectedParcelAPNs(data);
    })();
  }, [selectedIds]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    (async () => {
      const p = (await apiGetProject(projectId)) as Project;
      setProject(p);
    })();
  }, [projectId, selectedIds]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const hasTitle = useMemo(() => {
    if (selectedIds.size === 1) {
      const selectedId = Array.from(selectedIds)[0];
      const parcel = parcelDetails.find((p) => p.uniqueId === selectedId);
      if (parcel && !isEmpty(parcel.titleJson)) {
        return true;
      }
    } else {
      return false;
    }
  }, [selectedIds.size, parcelDetails, projectId]);

  const makeParcelRequest = async ({ docType }: { docType: DocType }) => {
    await buildParcelRequest({
      type: docType,
      projectId,
      parcelDetails: parcelDetails as ParcelDetails[],
      selectedIds,
      setIsOpen,
      currentOrganizationId: currentOrganization?.id ?? '',
      setProject,
      isSandbox: isSandboxOrg(),
      viaSignedUrl: titleZipsViaS3,
    });
  };

  interface MakeReprintRequestProps {
    parcelId: string;
    zip: string;
  }

  const makeReprintRequest = ({ parcelId, zip }: MakeReprintRequestProps) => {
    reprintTitle({
      parcelId,
      zip,
      projectId,
    });
  };

  if (selectedParcelAPNs.length === 0) {
    return (
      <IconButton disabled icon="file" variant="secondary" data-testid="land-rights-get-documents-button">
        Get Documents
      </IconButton>
    );
  }

  return (
    <>
      <Dropdown
        triggerElement={
          <IconButton disabled={isOpen} icon="file" variant="secondary" data-testid="land-rights-get-documents-button">
            Get Documents
          </IconButton>
        }
        items={[
          {
            type: 'dialog',
            label: 'Prelim Title Report',
            isDisabled: selectedIds.size < 1,
            dialogTitle: 'Prelim Title Report',
            dialogContent: (closeDialog) => (
              <>
                <DialogBody>
                  <Content docType={DocType.PrelimTitleReport} />
                </DialogBody>
                <DialogFooter>
                  <Buttons
                    closeDialog={closeDialog}
                    projectId={projectId}
                    docType={DocType.PrelimTitleReport}
                    project={project}
                    setProject={setProject}
                    makeParcelRequest={makeParcelRequest}
                  />
                </DialogFooter>
              </>
            ),
          },
          {
            type: 'dialog',
            label: hasTitle ? 'View Chain of Title' : 'Order Chain of Title',
            isDisabled: selectedIds.size !== 1,
            dialogTitle: hasTitle ? 'View Chain of Title' : 'Confirm Chain of Title Download',
            dialogContent: (closeDialog) => {
              const selectedId = selectedParcelAPNs[0].split('--')[0];
              const parcelDetail = project?.parcelDetails.filter((p) => p.parcelId === selectedId);
              return (
                <>
                  <DialogBody className="flex flex-col overflow-hidden">
                    <Content
                      docType={DocType.ChainOfTitleReport}
                      titleJson={parcelDetail?.[0]?.titleJson}
                      onOrderDocument={(docParams) => {
                        setDocToOrder(docParams);
                        setIsDocModalOpen(true);
                      }}
                      onOrderDocuments={(docsParams) => {
                        setDocsToOrder(docsParams);
                        setIsDocsModalOpen(true);
                      }}
                    />
                  </DialogBody>
                  <DialogFooter>
                    <Buttons
                      closeDialog={closeDialog}
                      projectId={projectId}
                      docType={DocType.ChainOfTitleReport}
                      project={project}
                      setProject={setProject}
                      makeParcelRequest={makeParcelRequest}
                      makeReprintRequest={() => {
                        if (!parcelDetail) return;
                        makeReprintRequest({
                          parcelId: parcelDetail[0].parcelId,
                          //@ts-expect-error
                          zip: pickFatZip(parcelDetail[0]) ?? parcelDetail[0].physzip,
                        });
                      }}
                    />
                  </DialogFooter>
                </>
              );
            },
            onItemSelect: () => {
              const selectedId = selectedParcelAPNs[0].split('--')[0];
              const parcelDetail = project?.parcelDetails.filter((p) => p.parcelId === selectedId);
              const isTitleInfoViewed = (hasTitle && parcelDetail && 'titleJson' in parcelDetail[0]) ?? false;
              if (isTitleInfoViewed) {
                store.updateDocumentOrderStep(OrderStep.View);
              }
            },
          },
          {
            type: 'dialog',
            label: 'Order Assessor Map',
            dialogTitle: 'Confirm Map Download',
            dialogContent: (closeDialog) => (
              <>
                <DialogBody>
                  <Content docType={DocType.MapReport} />
                </DialogBody>
                <DialogFooter>
                  <Buttons
                    closeDialog={closeDialog}
                    projectId={projectId}
                    docType={DocType.MapReport}
                    project={project}
                    setProject={setProject}
                    makeParcelRequest={makeParcelRequest}
                  />
                </DialogFooter>
              </>
            ),
          },
          {
            type: 'dialog',
            label: 'Order Lien Report',
            dialogTitle: 'Confirm Open Lien Report Download',
            dialogContent: (closeDialog) => (
              <>
                <DialogBody>
                  <Content docType={DocType.OpenLienReport} />
                </DialogBody>
                <DialogFooter>
                  <Buttons
                    closeDialog={closeDialog}
                    projectId={projectId}
                    docType={DocType.OpenLienReport}
                    project={project}
                    setProject={setProject}
                    makeParcelRequest={makeParcelRequest}
                  />
                </DialogFooter>
              </>
            ),
          },
          {
            type: 'dialog',
            label: 'Order Transaction History',
            dialogTitle: 'Confirm Transaction History Report Download',
            dialogContent: (closeDialog) => (
              <>
                <DialogBody>
                  <Content docType={DocType.TransactionHistoryReport} />
                </DialogBody>
                <DialogFooter>
                  <Buttons
                    closeDialog={closeDialog}
                    projectId={projectId}
                    docType={DocType.TransactionHistoryReport}
                    project={project}
                    setProject={setProject}
                    makeParcelRequest={makeParcelRequest}
                  />
                </DialogFooter>
              </>
            ),
          },
        ]}
        setIsOpen={(value) => {
          setIsOpen(value);
          if (value === false) {
            store.updateDocumentOrderStep(OrderStep.Confirm);
          }
        }}
      />
      <ConfirmDocumentOrderModal
        isDocModalOpen={isDocModalOpen}
        setIsDocModalOpen={setIsDocModalOpen}
        docParams={docToOrder}
        projectId={projectId}
        currentOrganizationId={currentOrganization?.id ?? ''}
      />
      <ConfirmDocumentsOrderModal
        isDocModalOpen={isDocsModalOpen}
        setIsDocModalOpen={setIsDocsModalOpen}
        docsParams={docsToOrder}
        projectId={projectId}
        currentOrganizationId={currentOrganization?.id ?? ''}
      />
    </>
  );
}
